/**
 * SOLID Review
 * S — Strong. One use case: roll first after a reveal — the plan with no floor, the server's next round.
 * O — Strong. The round trip runs it; a change of rule is the Domain's, of sequence the round trip's.
 * L — Strong. Implements its use-case role and the round trip's three strategies exactly.
 * I — Strong. `execute`, and the three strategies the round trip asks for.
 * D — Strong. The decisions and server ports and the round trip, injected.
 */
import type { IContextRequest } from '../ports/IContextRequest';
import type { IPropsClientTableInput } from '../../domain/ports/schemas/ClientTable';
import type { ICommandBelieveAccepted, ICommandNothing, IPayloadBelieve } from '../../domain/ports/schemas/TableMoves';
import type { TPortOutcome } from '../ports/PortResult';
import type { IUseCase } from '../uml-components/IUseCase';
import type { TServerRoundTrip, TAnswered } from '../shared/ServerRoundTrip';
import type { IConfigDepTable } from './IConfigDepTable';

/** The next roller rolls first after a reveal (FE1's rollFirst): the server's believe, with no floor. */
export class RollFirstUseCase implements IUseCase<ICommandNothing, TPortOutcome>, TServerRoundTrip<IPropsClientTableInput, ICommandNothing, IPayloadBelieve, TAnswered<ICommandBelieveAccepted['answer']>> {
  constructor(private readonly deps: IConfigDepTable) {}

  execute(ctx: IContextRequest, command: ICommandNothing): Promise<TPortOutcome> {
    return this.deps.trip.run(ctx, command, this);
  }

  plan(ctx: IContextRequest, input: IPropsClientTableInput, command: ICommandNothing) {
    return this.deps.decisions.planRollFirst(ctx, input, command);
  }

  send(ctx: IContextRequest, plan: IPayloadBelieve) {
    return this.deps.rounds.believeClaim(ctx, plan);
  }

  answer(ctx: IContextRequest, input: IPropsClientTableInput, plan: IPayloadBelieve, answered: TAnswered<ICommandBelieveAccepted['answer']>) {
    const accepted: ICommandBelieveAccepted = { request: plan, answer: answered.value };
    return this.deps.decisions.rollFirstAccepted(ctx, input, accepted);
  }
}
